import { auth, db } from '../config/firebase';
import { doc, getDoc, runTransaction, updateDoc, deleteField, setDoc } from 'firebase/firestore';

export const SESSION_ID_STORAGE_KEY = 'PE_ACTIVE_SESSION_ID';
export const SESSION_TTL_MS = 3 * 60 * 1000; // 180 Sekunden
export const SESSION_HEARTBEAT_MS = 30 * 1000;
export const SESSION_BLOCKED_CODE = 'session-blocked';

export type ActiveSession = {
    userId: string;
    sessionId: string;
    timestamp: number;
    /** true = Holder ist Admin/Coach; false/fehlend = Mandant */
    holderIsTeam?: boolean;
    /** Anzeigename (Coach/Admin), damit Sperr-Modal „Martina“ statt anonym zeigt */
    holderName?: string;
};

export type SessionClaimOpts = {
    userId: string;
    /** God-Mode / Impersonation — Claimant ist Team */
    isGodMode: boolean;
    /** Explizit Team-Claimant (Admin/Coach); Default = isGodMode */
    claimantIsTeam?: boolean;
    /** Anzeigename für Team-Claims (z. B. „Martina“) */
    holderName?: string;
};

const toTimestampMs = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (value && typeof (value as { toMillis?: () => number }).toMillis === 'function') {
        try {
            return (value as { toMillis: () => number }).toMillis();
        } catch {
            return 0;
        }
    }
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
};

export const getOrCreateLocalSessionId = (): string => {
    try {
        const existing = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
        if (existing) return existing;
        const created =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        sessionStorage.setItem(SESSION_ID_STORAGE_KEY, created);
        return created;
    } catch {
        return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    }
};

export const isSessionBlockedError = (err: any): boolean => {
    if (!err) return false;
    if (err.code === SESSION_BLOCKED_CODE) return true;
    const msg = String(err.message || err || '');
    return msg.includes('SESSION_BLOCKED') || msg.includes(SESSION_BLOCKED_CODE);
};

const throwSessionBlocked = (): never => {
    const err: any = new Error('SESSION_BLOCKED');
    err.code = SESSION_BLOCKED_CODE;
    throw err;
};

const resolveClaimantIsTeam = (opts: SessionClaimOpts): boolean =>
    opts.claimantIsTeam === true || opts.isGodMode === true;

const isLiveSession = (activeSession: Partial<ActiveSession> | null | undefined): boolean => {
    if (!activeSession?.sessionId || !activeSession?.userId) return false;
    const ts = toTimestampMs(activeSession.timestamp);
    if (!ts) return false;
    return Date.now() - ts <= SESSION_TTL_MS;
};

/** Lesbarer Name aus activeSession — für Overlay / AktivCounter. */
export const resolveActiveSessionHolderLabel = (
    active: Partial<ActiveSession> | null | undefined
): string | null => {
    if (!isLiveSession(active)) return null;
    const name = String(active?.holderName || '').trim();
    if (name) return name;
    if (active?.holderIsTeam === true) return 'PreisExpert-Team';
    return null;
};

/**
 * Block-Regeln (PEBase-Parität):
 * - Mandant:
 *   - Team-Session (holderIsTeam) blockiert NIE — Admin-Arbeit läuft über isLockedByAdmin
 *     (System-Wartung). Sonst False-Positive „anderes Gerät“, obwohl AktivCounter keinen
 *     zweiten Mandanten zeigt und nur ein hängender God-Mode-Lock übrig ist.
 *   - Gleiche Person (Reload / Tab / PWA / PEBase↔PEOne) darf reclaimen.
 *   - Nur eine *andere* Nicht-Team-Session mit anderer userId blockiert.
 * - Team (God/Coach/Admin):
 *   - Mandanten-Lock → Silent Force-Claim
 *   - Anderer Team-Holder → blockieren
 *   - Gleicher Team-User (Multi-Tab) → nicht blockieren
 */
export const isForeignSessionBlocking = (
    activeSession: Partial<ActiveSession> | null | undefined,
    myUserId: string,
    mySessionId: string,
    isGodModeOrOpts: boolean | SessionClaimOpts
): boolean => {
    if (!activeSession?.sessionId || !activeSession?.userId) return false;
    if (activeSession.sessionId === mySessionId) return false;
    if (!isLiveSession(activeSession)) return false;

    const opts: SessionClaimOpts =
        typeof isGodModeOrOpts === 'boolean'
            ? { userId: myUserId, isGodMode: isGodModeOrOpts }
            : isGodModeOrOpts;
    const claimantIsTeam = resolveClaimantIsTeam(opts);

    if (claimantIsTeam) {
        // Mandant hält Lock (oder Legacy ohne Flag) → Silent Bypass
        if (activeSession.holderIsTeam !== true) return false;
        // Anderer Coach/Admin → Kollision
        return activeSession.userId !== myUserId;
    }

    // Mandant: Team-Locks ignorieren (Wartungs-Overlay ist die echte Admin-Sperre)
    if (activeSession.holderIsTeam === true) return false;

    // Mandant: nur andere Person blockiert — eigener Reload/Tab/App darf reclaimen
    return activeSession.userId !== myUserId;
};

/** Read-only: fremde Session aktiv? */
export const checkRemoteSessionBlocking = async (
    mandantUid: string,
    opts: SessionClaimOpts
): Promise<boolean> => {
    const sessionId = getOrCreateLocalSessionId();
    try {
        const snap = await getDoc(doc(db, 'kalkulation_mandanten', mandantUid));
        if (!snap.exists()) return false;
        const active = (snap.data()?.activeSession || null) as Partial<ActiveSession> | null;
        return isForeignSessionBlocking(active, opts.userId, sessionId, opts);
    } catch (err) {
        console.error('checkRemoteSessionBlocking fehlgeschlagen:', err);
        return false;
    }
};

/**
 * Claim / Silent Force-Claim:
 * Team überschreibt Mandanten-Locks lautlos; Team-vs-Team bleibt gesperrt.
 */
export const tryClaimActiveSession = async (
    mandantUid: string,
    opts: SessionClaimOpts
): Promise<{ ok: true } | { ok: false }> => {
    const sessionId = getOrCreateLocalSessionId();
    const docRef = doc(db, 'kalkulation_mandanten', mandantUid);
    const claimantIsTeam = resolveClaimantIsTeam(opts);
    const holderName = String(opts.holderName || '').trim();
    const payload: ActiveSession = {
        userId: opts.userId,
        sessionId,
        timestamp: Date.now(),
        holderIsTeam: claimantIsTeam,
        ...(holderName ? { holderName } : {}),
    };

    try {
        await runTransaction(db, async (transaction) => {
            const snap = await transaction.get(docRef);
            const data = snap.exists() ? snap.data() : null;
            const active = (data?.activeSession || null) as Partial<ActiveSession> | null;

            if (isForeignSessionBlocking(active, opts.userId, sessionId, opts)) {
                throwSessionBlocked();
            }

            // inkl. Silent Overwrite wenn Mandant hält / TTL abgelaufen / leer
            transaction.set(docRef, { activeSession: payload }, { merge: true });
        });

        return { ok: true };
    } catch (err: any) {
        if (isSessionBlockedError(err)) {
            return { ok: false };
        }
        console.error('tryClaimActiveSession Transaction fehlgeschlagen:', err);

        try {
            const snap = await getDoc(docRef);
            const active = snap.exists()
                ? ((snap.data()?.activeSession || null) as Partial<ActiveSession> | null)
                : null;
            if (isForeignSessionBlocking(active, opts.userId, sessionId, opts)) {
                return { ok: false };
            }
            await updateDoc(docRef, { activeSession: { ...payload, timestamp: Date.now() } });
            return { ok: true };
        } catch (fallbackErr) {
            console.error('tryClaimActiveSession Fallback fehlgeschlagen:', fallbackErr);
            const remoteBlocking = await checkRemoteSessionBlocking(mandantUid, opts);
            if (remoteBlocking) return { ok: false };
            return { ok: true };
        }
    }
};

export const heartbeatActiveSession = async (
    mandantUid: string,
    userId: string,
    opts?: { holderIsTeam?: boolean; holderName?: string }
): Promise<void> => {
    const sessionId = getOrCreateLocalSessionId();
    const docRef = doc(db, 'kalkulation_mandanten', mandantUid);
    const claimOpts: SessionClaimOpts = {
        userId,
        isGodMode: opts?.holderIsTeam === true,
        claimantIsTeam: opts?.holderIsTeam === true,
        holderName: opts?.holderName,
    };
    const holderName = String(opts?.holderName || '').trim();
    try {
        await runTransaction(db, async (transaction) => {
            const snap = await transaction.get(docRef);
            if (!snap.exists()) return;
            const active = (snap.data()?.activeSession || null) as Partial<ActiveSession> | null;

            // Fremde Live-Session (andere Person / anderer Coach) → nicht anfassen
            if (isForeignSessionBlocking(active, userId, sessionId, claimOpts)) {
                return;
            }

            // Eigene Session renewen ODER leere/abgelaufene/eigene Stale-Session neu claimen
            transaction.set(
                docRef,
                {
                    activeSession: {
                        userId,
                        sessionId,
                        timestamp: Date.now(),
                        // Nie holderIsTeam von vorherigem Coach erben — sonst sticky Team-Lock
                        holderIsTeam: opts?.holderIsTeam === true,
                        ...(holderName
                            ? { holderName }
                            : active?.holderName
                              ? { holderName: String(active.holderName) }
                              : {}),
                    } satisfies ActiveSession,
                },
                { merge: true }
            );
        });
    } catch (err) {
        console.error('heartbeatActiveSession fehlgeschlagen:', err);
    }
};

/** Gibt false zurück, wenn der Write abgebrochen werden muss. */
export const assertLocalSessionOwnsWrite = async (
    mandantUid: string,
    opts: { skipForGodMode: boolean; userId?: string; claimantIsTeam?: boolean }
): Promise<boolean> => {
    if (opts.skipForGodMode) return true;

    const localSessionId = getOrCreateLocalSessionId();
    const userId = opts.userId || auth.currentUser?.uid || '';
    if (!userId) return false;

    const claimOpts: SessionClaimOpts = {
        userId,
        isGodMode: opts.claimantIsTeam === true,
        claimantIsTeam: opts.claimantIsTeam === true,
    };

    try {
        const snap = await getDoc(doc(db, 'kalkulation_mandanten', mandantUid));
        if (!snap.exists()) return false;
        const remote = snap.data()?.activeSession as Partial<ActiveSession> | undefined;

        if (remote?.sessionId === localSessionId) return true;

        // Keine / abgelaufene / eigene-verloren Session: vor dem Write still neu claimen.
        // Verhindert FEHLER-Badge bei legitimen Speichern (z. B. PEPapers-Freischaltung),
        // wenn Heartbeat die Session noch nicht hält oder TTL abgelaufen ist.
        if (isForeignSessionBlocking(remote, userId, localSessionId, claimOpts)) {
            return false;
        }

        const claim = await tryClaimActiveSession(mandantUid, claimOpts);
        if (!claim.ok) return false;

        const after = await getDoc(doc(db, 'kalkulation_mandanten', mandantUid));
        const afterSession = after.exists()
            ? (after.data()?.activeSession as Partial<ActiveSession> | undefined)
            : undefined;
        return afterSession?.sessionId === localSessionId;
    } catch (err) {
        console.error('assertLocalSessionOwnsWrite fehlgeschlagen:', err);
        return false;
    }
};

/**
 * Eigene Session freigeben.
 * - Default: nur wenn sessionId zu diesem Tab passt (Mandant Multi-Tab sicher).
 * - matchUserId: Team/God-Mode — alle eigenen Locks auf dieser Akte löschen
 *   (sonst bleibt Tab-A-Session hängen, wenn Tab-B „Beenden“ drückt).
 */
export const clearOwnedActiveSession = async (
    mandantUid: string,
    opts?: { matchUserId?: string }
): Promise<void> => {
    const localSessionId = getOrCreateLocalSessionId();
    const docRef = doc(db, 'kalkulation_mandanten', mandantUid);
    try {
        const snap = await getDoc(docRef);
        if (!snap.exists()) return;
        const remote = snap.data()?.activeSession as Partial<ActiveSession> | undefined;
        if (!remote?.sessionId) return;

        if (opts?.matchUserId) {
            if (remote.userId !== opts.matchUserId) return;
        } else if (remote.sessionId !== localSessionId) {
            return;
        }

        await updateDoc(docRef, { activeSession: deleteField() });
    } catch (err) {
        console.error('clearOwnedActiveSession fehlgeschlagen:', err);
    }
};

// ============================================================================
// ADMIN-WARTUNGS-LOCK (isLockedByAdmin) — mit Heartbeat/TTL, kein ewiger Sticky-Lock
// ============================================================================

/** Gleicher TTL wie Session-Lock: ohne Heartbeat fällt Wartung nach ~3 Min. */
export const ADMIN_LOCK_TTL_MS = SESSION_TTL_MS;
export const ADMIN_LOCK_HEARTBEAT_MS = SESSION_HEARTBEAT_MS;

/**
 * Coach/Admin ohne Interaktion so lange → Wartungs-Lock + Team-Session freigeben,
 * damit der Mandant die Akte übernehmen kann (Tab offen ≠ aktiv).
 * Kurz gehalten, damit Geister-Locks (vergessener Tab) Kunden nicht lange blockieren.
 */
export const ADMIN_LOCK_IDLE_MS = 5 * 60 * 1000; // 5 Minuten Tatenlosigkeit

const toAdminLockHeartbeatMs = (value: unknown): number => toTimestampMs(value);

export type AdminLockHolder = {
    uid: string;
    name: string;
    email?: string;
};

export type AdminLockSnap = {
    isLockedByAdmin?: unknown;
    adminLockHeartbeat?: unknown;
    adminLockHolderUid?: unknown;
    adminLockHolderName?: unknown;
};

/**
 * System-Wartung nur bei frischem Admin-Heartbeat.
 * Legacy ohne adminLockHeartbeat (hängengebliebene Flags) = NICHT live → Mandant kommt rein.
 */
export const isAdminMaintenanceLockLive = (
    data: AdminLockSnap | null | undefined
): boolean => {
    if (!data || data.isLockedByAdmin !== true) return false;
    const hb = toAdminLockHeartbeatMs(data.adminLockHeartbeat);
    if (!hb) return false;
    return Date.now() - hb <= ADMIN_LOCK_TTL_MS;
};

/** Anzeigename aus Lock-Snapshot (für Overlay / AktivCounter). */
export const resolveAdminLockHolderLabel = (
    data: AdminLockSnap | null | undefined
): string | null => {
    if (!isAdminMaintenanceLockLive(data)) return null;
    const name = String(data?.adminLockHolderName || '').trim();
    if (name) return name;
    return 'PreisExpert-Team';
};

/** Payload zum Setzen/Renewen des Wartungs-Locks. */
export const buildAdminLockPayload = (
    locked: boolean,
    holder?: AdminLockHolder | null
): Record<string, unknown> => {
    if (locked) {
        const payload: Record<string, unknown> = {
            isLockedByAdmin: true,
            adminLockHeartbeat: Date.now(),
        };
        if (holder?.uid) payload.adminLockHolderUid = holder.uid;
        if (holder?.name) payload.adminLockHolderName = holder.name;
        return payload;
    }
    return {
        isLockedByAdmin: false,
        adminLockHeartbeat: deleteField(),
        adminLockHolderUid: deleteField(),
        adminLockHolderName: deleteField(),
    };
};

/**
 * God-Mode-Heartbeat: Lock aktiv halten solange Impersonation läuft und Tab sichtbar ist.
 * Schreibt parallel lastActiveAt auf users/{holder} — sonst bleibt AktivCounter bei
 * Coaches ohne users-Heartbeat auf „Keine Aktivität“ (klassischer Jean-Bug).
 */
export const heartbeatAdminMaintenanceLock = async (
    mandantUid: string,
    holder?: AdminLockHolder | null
): Promise<void> => {
    if (!mandantUid) return;
    try {
        await updateDoc(
            doc(db, 'kalkulation_mandanten', mandantUid),
            buildAdminLockPayload(true, holder) as any
        );
    } catch (err) {
        console.error('heartbeatAdminMaintenanceLock fehlgeschlagen:', err);
    }
    if (holder?.uid) {
        try {
            const presencePayload: Record<string, unknown> = {
                lastActiveAt: Date.now(),
                lastAktePresence: {
                    mandantUid,
                    at: Date.now(),
                    name: holder.name || null,
                },
            };
            if (holder.email) presencePayload.email = holder.email;
            if (holder.name) presencePayload.displayName = holder.name;
            await setDoc(doc(db, 'users', holder.uid), presencePayload, { merge: true });
        } catch (err2) {
            console.warn('God-Mode users-Presence fehlgeschlagen:', err2);
        }
    }
};

/** Wartungs-Lock freigeben (Idle-Timeout / Tab zu / BEENDEN). */
export const releaseAdminMaintenanceLock = async (mandantUid: string): Promise<void> => {
    if (!mandantUid) return;
    try {
        await updateDoc(doc(db, 'kalkulation_mandanten', mandantUid), buildAdminLockPayload(false) as any);
    } catch (err) {
        console.error('releaseAdminMaintenanceLock fehlgeschlagen:', err);
    }
};
